import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { supabase, CellarTobacco, CellarPipe } from '../lib/supabase';
import Button from '../components/ui/Button';
import BottomNav from '../components/BottomNav';
import { ArrowLeft, NotebookPen, Image as ImageIcon, Star, Search, ChevronDown } from 'lucide-react';
import { isImageFile } from '../lib/imageCompression';
import { toast } from '../lib/toast';
import { searchBlends } from '../lib/blendSearch';
import type { BlendMatch } from '../lib/blendSearch';
import { loadDraftState, saveDraftState, clearDraftState } from '../lib/draftStateStore';
import { dataUrlToFile } from '../lib/draftPhotoStore';
import { usePhotoStorage, StoredPhoto } from '../hooks/usePhotoStorage';
import { usePhotoProcessor } from '../hooks/usePhotoProcessor';

const SESSION_DRAFT_BASE_KEY = 'ember.sessionDraft.v1';

interface SessionDraftStored {
  cellarId: string;
  tobaccoManualName: string;
  useManual: boolean;
  rating: number;
  notes: string;
  pipeManual: string;
  pipeInput: string;
  lighterManual: string;
  drinkPairing: string;
  photoPreview: string | null;
  photoName: string | null;
  photoType: string | null;
}

export default function NewSession() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { sessionId } = useParams();
  const prefillCellarId = params.get('cellar_tobacco_id');
  const sessionDraftKey = useMemo(
    () => (sessionId ? `${SESSION_DRAFT_BASE_KEY}.${sessionId}` : `${SESSION_DRAFT_BASE_KEY}.new`),
    [sessionId]
  );
  const draftLoadedRef = useRef(false);
  const { loadPhoto, loadPhotoSync, savePhoto, savePhotoData, clearPhoto } = usePhotoStorage(sessionDraftKey);
  const { processPhoto } = usePhotoProcessor(sessionDraftKey);
  const initialStoredPhoto = loadPhotoSync();
  const [photoData, setPhotoData] = useState<StoredPhoto | null>(initialStoredPhoto);
  const [photoFile, setPhotoFile] = useState<File | null>(() =>
    initialStoredPhoto ? dataUrlToFile(initialStoredPhoto.data, initialStoredPhoto.name, initialStoredPhoto.type) : null
  );
  const [photoPreview, setPhotoPreview] = useState<string | null>(initialStoredPhoto ? initialStoredPhoto.data : null);

  const [cellar, setCellar] = useState<CellarTobacco[]>([]);
  const [cellarId, setCellarId] = useState<string>(prefillCellarId || '');
  const [tobaccoManualName, setTobaccoManualName] = useState<string>('');
  const [useManual, setUseManual] = useState<boolean>(!!(!prefillCellarId));
  const [rating, setRating] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [pipeManual, setPipeManual] = useState('');
  const [pipeInput, setPipeInput] = useState('');
  const [showPipeMatches, setShowPipeMatches] = useState(false);
  const [cellarPipes, setCellarPipes] = useState<CellarPipe[]>([]);
  const [lighterManual, setLighterManual] = useState('');
  const [drinkPairing, setDrinkPairing] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [manualMatches, setManualMatches] = useState<BlendMatch[]>([]);
  const [showManualMatches, setShowManualMatches] = useState(false);
  const [manualSearching, setManualSearching] = useState(false);
  const [selectedManualMatch, setSelectedManualMatch] = useState<BlendMatch | null>(null);
  const manualSearchTimer = useRef<number | null>(null);
  const suppressManualSearchRef = useRef(false);
  const [showCellarDropdown, setShowCellarDropdown] = useState(false);
  const cellarPickerRef = useRef<HTMLDivElement | null>(null);

  const formatStorageType = (storage?: CellarTobacco['storage_type']) => {
    if (!storage) return '';
    return storage
      .split(/[_\s-]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const openCellarItems = useMemo(() => cellar.filter((item) => item.status === 'opened'), [cellar]);

  const cellarOptions = useMemo(() => {
    if (!cellarId) return openCellarItems;
    const selected = cellar.find((item) => item.id === cellarId);
    if (selected && selected.status !== 'opened') {
      const withoutSelected = openCellarItems.filter((item) => item.id !== selected.id);
      return [selected, ...withoutSelected];
    }
    return openCellarItems;
  }, [cellarId, cellar, openCellarItems]);

  const selectedCellar = useMemo(() => {
    if (!cellarId) return null;
    return cellar.find((item) => item.id === cellarId) ?? null;
  }, [cellarId, cellar]);

  const selectedCellarLabel = useMemo(() => {
    if (!selectedCellar) return '';
    const storage = formatStorageType(selectedCellar.storage_type);
    return `${selectedCellar.brand} - ${selectedCellar.blend_name}${storage ? ` (${storage})` : ''}`;
  }, [selectedCellar]);

  const displayedCellarOptions = useMemo(() => {
    if (!selectedCellar || cellarOptions.some((item) => item.id === selectedCellar.id)) {
      return cellarOptions;
    }
    return [selectedCellar, ...cellarOptions];
  }, [cellarOptions, selectedCellar]);

  useEffect(() => {
    loadCellar();
    loadCellarPipes();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (sessionId) {
          await loadExistingSession(sessionId);
          if (cancelled) {
            return;
          }
        }
        const stored = await loadDraftState<SessionDraftStored>(sessionDraftKey);
        if (cancelled) {
          return;
        }
        let photoName: string | null = null;
        let photoType: string | null = null;
        if (stored) {
          if (typeof stored.cellarId === 'string') setCellarId(stored.cellarId);
          if (typeof stored.tobaccoManualName === 'string') setTobaccoManualName(stored.tobaccoManualName);
          if (typeof stored.useManual === 'boolean') setUseManual(stored.useManual);
          if (typeof stored.rating === 'number') setRating(stored.rating);
          if (typeof stored.notes === 'string') setNotes(stored.notes);
          if (typeof stored.pipeManual === 'string') setPipeManual(stored.pipeManual);
          if (typeof stored.pipeInput === 'string') setPipeInput(stored.pipeInput);
          if (typeof stored.lighterManual === 'string') setLighterManual(stored.lighterManual);
          if (typeof stored.drinkPairing === 'string') setDrinkPairing(stored.drinkPairing);
          if ('photoPreview' in stored) setPhotoPreview(stored.photoPreview ?? null);
          if ('photoName' in stored) photoName = stored.photoName ?? null;
          if ('photoType' in stored) photoType = stored.photoType ?? null;
        }
        if (stored?.photoPreview) {
          const restoredPhoto: StoredPhoto = {
            name: (photoName && typeof photoName === 'string' && photoName.trim()) ? photoName : 'session-photo.jpg',
            type: (photoType && typeof photoType === 'string' && photoType.trim()) ? photoType : 'image/jpeg',
            data: stored.photoPreview,
            processed: true,
            processedAt: new Date().toISOString(),
          };
          void savePhotoData(restoredPhoto);
          setPhotoData(restoredPhoto);
        } else {
          const restored = await loadPhoto();
          if (cancelled) {
            return;
          }
          if (restored) {
            setPhotoData(restored);
          }
        }
      } catch (error) {
        console.warn('Failed to load session draft', error);
      } finally {
        if (!cancelled) {
          draftLoadedRef.current = true;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, sessionDraftKey, savePhotoData, loadPhoto]);

  useEffect(() => {
    if (prefillCellarId) setCellarId(prefillCellarId);
  }, [prefillCellarId]);

  useEffect(() => {
    if (useManual) {
      setShowCellarDropdown(false);
    }
  }, [useManual]);

  useEffect(() => {
    if (!showCellarDropdown) return;
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (!cellarPickerRef.current) return;
      if (!cellarPickerRef.current.contains(event.target as Node)) {
        setShowCellarDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCellarDropdown]);

  useEffect(() => {
    return () => {
      if (manualSearchTimer.current) {
        clearTimeout(manualSearchTimer.current);
        manualSearchTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!useManual) {
      if (manualSearchTimer.current) {
        clearTimeout(manualSearchTimer.current);
        manualSearchTimer.current = null;
      }
      setManualMatches([]);
      setShowManualMatches(false);
      setManualSearching(false);
      setSelectedManualMatch(null);
    }
  }, [useManual]);

  useEffect(() => {
    if (!draftLoadedRef.current) {
      return;
    }
    const draft: SessionDraftStored = {
      cellarId,
      tobaccoManualName,
      useManual,
      rating,
      notes,
      pipeManual,
      pipeInput,
      lighterManual,
      drinkPairing,
      photoPreview: photoData?.data ?? null,
      photoName: photoData?.name ?? null,
      photoType: photoData?.type ?? null,
    };
    void saveDraftState(sessionDraftKey, draft);
  }, [
    cellarId,
    tobaccoManualName,
    useManual,
    rating,
    notes,
    pipeManual,
    pipeInput,
    lighterManual,
    drinkPairing,
    photoData,
    sessionDraftKey,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!photoData) {
      setPhotoPreview(null);
      setPhotoFile(null);
      return;
    }

    setPhotoPreview(photoData.data);
    const restoredFile = dataUrlToFile(
      photoData.data,
      (photoData.name && photoData.name.trim()) ? photoData.name : 'session-photo.jpg',
      photoData.type || 'image/jpeg',
    );
    setPhotoFile(restoredFile);

    if (!photoData.processed) {
      setCompressing(true);
      processPhoto()
        .then((processed) => {
          if (!cancelled && processed) {
            setPhotoData(processed);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setCompressing(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [photoData, processPhoto]);

  async function loadCellar() {
    const { data } = await supabase.from('cellar_tobacco').select('*').order('created_at', { ascending: false });
    if (data) setCellar(data);
  }

  async function loadCellarPipes() {
    const { data } = await supabase
      .from('cellar_pipes')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setCellarPipes(data as any);
  }

  async function loadExistingSession(id: string) {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('id', id)
      .single();
    if (!error && data) {
      setCellarId(data.cellar_tobacco_id || '');
      setTobaccoManualName(data.tobacco_manual || '');
      setUseManual(!data.cellar_tobacco_id && !!data.tobacco_manual);
      setRating(data.rating_10 || data.rating_small || 0);
      setNotes(data.notes || '');
      setPipeManual(data.pipe_manual || '');
      setLighterManual(data.lighter_manual || '');
      setDrinkPairing(data.drink_pairing || '');
      // We don't pre-fill photo preview (no download), user can replace
    }
  }

  function queueManualSearch(query: string) {
    if (manualSearchTimer.current) {
      clearTimeout(manualSearchTimer.current);
    }
    setManualSearching(true);
    manualSearchTimer.current = window.setTimeout(async () => {
      const { data, error } = await searchBlends(query);
      if (error || !data || data.length === 0) {
        setManualMatches([]);
        setShowManualMatches(false);
      } else {
        setManualMatches(data);
        setShowManualMatches(true);
      }
      setManualSearching(false);
    }, 250);
  }

  function handleManualInputChange(value: string) {
    setTobaccoManualName(value);
    setShowManualMatches(true);

    if (!value.trim()) {
      setSelectedManualMatch(null);
      setManualMatches([]);
      setShowManualMatches(false);
      setManualSearching(false);
      return;
    }

    if (suppressManualSearchRef.current) {
      suppressManualSearchRef.current = false;
      return;
    }

    setSelectedManualMatch(null);

    if (value.trim().length < 2) {
      setManualMatches([]);
      setManualSearching(false);
      return;
    }

    queueManualSearch(value);
  }

  function selectManualMatch(match: BlendMatch) {
    setSelectedManualMatch(match);
    setTobaccoManualName(`${match.brand} - ${match.blend_name}`);
    suppressManualSearchRef.current = true;
    setShowManualMatches(false);
    setManualMatches([]);
  }

  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) {
      setPhotoData(null);
      setPhotoPreview(null);
      setPhotoFile(null);
      clearPhoto();
      return;
    }
    if (!isImageFile(f)) {
      toast.error('Please select an image');
      return;
    }
    setCompressing(true);
    try {
      const stored = await savePhoto(f);
      if (stored) {
        setPhotoData(stored);
      }
    } catch (err) {
      console.error('Image save failed', err);
      toast.error('Failed to process image');
    } finally {
      setCompressing(false);
    }
  }

  async function handleSubmit() {
    if (!cellarId && !tobaccoManualName.trim()) { toast.error('Select a cellar item or enter manually'); return; }
    if (!rating) { toast.error('Select a rating'); return; }
    setLoading(true);
    try {
      let photo_url: string | null = null;
      if (photoFile) {
        const filePath = `${(await supabase.auth.getUser()).data.user?.id}/${Date.now()}-${photoFile.name}`;
        const { data: up, error: upErr } = await supabase.storage.from('checkin-photos').upload(filePath, photoFile, { upsert: false });
        if (!upErr && up) {
          const { data: pub } = supabase.storage.from('checkin-photos').getPublicUrl(up.path);
          photo_url = pub?.publicUrl ?? null;
        }
      }
      if (sessionId) {
        const { error } = await supabase.from('sessions').update({
          cellar_tobacco_id: cellarId || null,
          tobacco_manual: tobaccoManualName.trim() || null,
          rating_10: rating,
          notes: notes.trim() || null,
          pipe_manual: pipeManual.trim() || null,
          lighter_manual: lighterManual.trim() || null,
          drink_pairing: drinkPairing.trim() || null,
          // Only update photo_url if user picked a new photo
          ...(photo_url ? { photo_url } : {}),
        }).eq('id', sessionId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('sessions').insert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          cellar_tobacco_id: cellarId || null,
          tobacco_manual: tobaccoManualName.trim() || null,
          rating_10: rating,
          notes: notes.trim() || null,
          pipe_manual: pipeManual.trim() || null,
          lighter_manual: lighterManual.trim() || null,
          drink_pairing: drinkPairing.trim() || null,
          photo_url,
        });
        if (error) throw error;
      }
      toast.success('Session saved');
      draftLoadedRef.current = false;
      await Promise.all([
        clearDraftState(sessionDraftKey),
      ]);
      clearPhoto();
      setPhotoData(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      navigate('/sessions');
    } catch (err) {
      console.error('Save session failed', err);
      toast.error('Failed to save');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '120px' }}>
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid #1f1f1f',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
      }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}>
          <ArrowLeft size={24} />
        </button>
        <NotebookPen size={24} color="var(--color-accent)" />
        <h1 style={{ fontSize: '1.5rem' }}>{sessionId ? 'Edit Session' : 'New Session'}</h1>
      </header>

      <div className="container" style={{ paddingTop: '1rem' }}>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Tobacco</label>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              aria-pressed={!useManual}
              onClick={() => setUseManual(false)}
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid var(--color-border)',
                backgroundColor: !useManual ? 'var(--color-accent)' : 'var(--color-card)',
                color: !useManual ? '#0b0b0b' : '#ccc',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              From My Cellar
            </button>
            <button
              type="button"
              aria-pressed={useManual}
              onClick={() => setUseManual(true)}
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid var(--color-border)',
                backgroundColor: useManual ? 'var(--color-accent)' : 'var(--color-card)',
                color: useManual ? '#0b0b0b' : '#ccc',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              Enter Manually
            </button>
          </div>
          {!useManual ? (
            <div ref={cellarPickerRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowCellarDropdown((prev) => !prev)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: showCellarDropdown ? 'rgba(196, 255, 0, 0.12)' : 'var(--color-card)',
                  border: `1px solid ${showCellarDropdown ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  borderRadius: '6px',
                  color: selectedCellarLabel ? 'var(--color-text)' : '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  fontSize: '0.95rem',
                  cursor: 'pointer'
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedCellarLabel || 'Select from your cellar…'}
                </span>
                <ChevronDown
                  size={18}
                  color="var(--color-accent)"
                  style={{ transform: showCellarDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                />
              </button>
              {showCellarDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 'calc(100% + 4px)',
                    zIndex: 1000,
                    backgroundColor: '#1a1a1a',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)'
                  }}
                >
                  {displayedCellarOptions.length === 0 && (
                    <div style={{ padding: '0.75rem', color: '#bbb', fontSize: '0.85rem' }}>
                      No opened cellar items available.
                    </div>
                  )}
                  {displayedCellarOptions.map((item) => {
                    const storage = formatStorageType(item.storage_type);
                    const label = `${item.brand} - ${item.blend_name}${storage ? ` (${storage})` : ''}`;
                    const isSelected = cellarId === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setCellarId(item.id);
                          setShowCellarDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.75rem',
                          border: 'none',
                          background: isSelected ? 'rgba(196, 255, 0, 0.12)' : 'transparent',
                          color: '#fff',
                          cursor: 'pointer',
                          fontWeight: isSelected ? 600 : 500
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                  {cellarId && (
                    <div style={{ borderTop: '1px solid var(--color-border)' }}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setCellarId('');
                          setShowCellarDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: 'none',
                          background: 'transparent',
                          color: '#ccc',
                          cursor: 'pointer',
                          fontStyle: 'italic'
                        }}
                      >
                        Clear selection
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <Search size={18} color="#666" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="text"
                value={tobaccoManualName}
                onChange={(e) => handleManualInputChange(e.target.value)}
                onFocus={() => setShowManualMatches(true)}
                onBlur={() => setTimeout(() => setShowManualMatches(false), 150)}
                placeholder="Type to search blends or enter manually…"
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  backgroundColor: selectedManualMatch ? 'rgba(196, 255, 0, 0.12)' : 'var(--color-card)',
                  border: selectedManualMatch ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
                  borderRadius: '6px',
                  color: 'var(--color-text)'
                }}
              />
              {manualSearching && (
                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                  <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                </div>
              )}
              {showManualMatches && manualMatches.length > 0 && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', zIndex: 1000, backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '6px', maxHeight: '240px', overflowY: 'auto' }}>
                  {manualMatches.map(match => {
                    const isSelected = selectedManualMatch?.id === match.id;
                    return (
                      <button
                        key={match.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          selectManualMatch(match);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.75rem',
                          border: 'none',
                          background: isSelected ? 'rgba(196, 255, 0, 0.08)' : 'transparent',
                          color: '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{match.brand} - {match.blend_name}</div>
                        {match.blend_type && (
                          <div style={{ fontSize: '0.75rem', color: '#ccc', marginTop: '0.15rem' }}>{match.blend_type}</div>
                        )}
                        {match.avg_rating != null && (
                          <div style={{ fontSize: '0.75rem', color: '#ff9500', marginTop: '0.15rem' }}>⭐ {match.avg_rating.toFixed(2)}</div>
                        )}
                      </button>
                    );
                  })}
                  <div style={{ borderTop: '1px solid var(--color-border)' }}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        suppressManualSearchRef.current = true;
                        setSelectedManualMatch(null);
                        setShowManualMatches(false);
                        setManualMatches([]);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: 'none',
                        background: 'transparent',
                        color: '#ccc',
                        fontStyle: 'italic',
                        cursor: 'pointer'
                      }}
                    >
                      Keep manual entry
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Rating</label>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
            {[1,2,3,4,5,6,7,8,9,10].map(star => (
              <button key={star} type="button" onClick={() => setRating(star)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <Star size={24} fill={star <= rating ? '#ff9500' : 'none'} color={star <= rating ? '#ff9500' : '#6b4423'} />
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Notes</label>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ width: '100%', padding: '0.75rem', backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'white', fontSize: '0.875rem', resize: 'vertical', fontFamily: 'inherit' }} placeholder="Your private notes…" />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Pipe (Optional)</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={pipeInput}
              onChange={(e) => {
                setPipeInput(e.target.value);
                setPipeManual(e.target.value);
                setShowPipeMatches(true);
              }}
              placeholder="Type to search your cellar or enter manually..."
              style={{ width: '100%' }}
              onFocus={() => setShowPipeMatches(true)}
              onBlur={() => setTimeout(() => setShowPipeMatches(false), 150)}
            />
            {showPipeMatches && pipeInput.trim().length > 0 && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1000, backgroundColor: '#1a1a1a', border: '1px solid var(--color-border)', borderRadius: '6px', maxHeight: '220px', overflowY: 'auto' }}>
                {cellarPipes
                  .filter(p => `${p.maker} ${p.shape} ${p.style || ''}`.toLowerCase().includes(pipeInput.toLowerCase()))
                  .slice(0, 12)
                  .map(p => (
                    <button key={p.id} type="button" onMouseDown={() => {
                      setPipeManual(`${p.maker} ${p.shape}${p.style ? ` (${p.style})` : ''}`);
                      setPipeInput(`${p.maker} ${p.shape}${p.style ? ` (${p.style})` : ''}`);
                      setShowPipeMatches(false);
                    }} style={{ width: '100%', textAlign: 'left', padding: '0.5rem', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                      {p.maker} {p.shape} {p.style && `(${p.style})`}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Lighter/Matches (Optional)</label>
          <input value={lighterManual} onChange={(e) => setLighterManual(e.target.value)} placeholder="e.g., Zippo Pipe Insert" style={{ width: '100%' }} />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Paired With (Optional)</label>
          <input value={drinkPairing} onChange={(e) => setDrinkPairing(e.target.value)} placeholder="e.g., Coffee, Whiskey…" style={{ width: '100%' }} />
        </div>

        {/* Photo */}
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Photo (Optional)</label>
          {photoPreview && (
            <div className="card-photo" style={{ marginBottom: '0.75rem' }}>
              <img src={photoPreview} alt="Preview" style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px', backgroundColor: '#1f1f1f' }} />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <label htmlFor="session-photo-gallery" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1rem', border: '2px dashed #333', borderRadius: '8px', cursor: 'pointer', backgroundColor: 'var(--color-card)' }}>
              <ImageIcon size={24} color="var(--color-accent)" />
              <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Choose from Gallery</span>
            </label>
            <label htmlFor="session-photo-camera" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1rem', border: '2px dashed #333', borderRadius: '8px', cursor: 'pointer', backgroundColor: 'var(--color-card)' }}>
              <ImageIcon size={24} color="var(--color-accent)" />
              <span style={{ fontSize: '0.875rem', textAlign: 'center' }}>Take Photo</span>
            </label>
          </div>
          <input id="session-photo-gallery" type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
          <input id="session-photo-camera" type="file" accept="image/*" capture="environment" onChange={handlePhotoChange} style={{ display: 'none' }} />
        </div>

        <Button onClick={handleSubmit} loading={loading || compressing} disabled={(!cellarId && !tobaccoManualName.trim()) || !rating} fullWidth>
          {sessionId ? 'Save' : 'Save Session'}
        </Button>
        </form>
      </div>

      <BottomNav />
    </div>
  );
}


